import RHFDatePicker from '@/components/date-picker/RHFDatePicker';
import Spinner from '@/components/spinner/Spinner';
import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Suspense } from 'react';
import { Await } from 'react-router';
import { UseFormReturn } from 'react-hook-form';
import { AxiosResponse } from 'axios';
import { CompactProcessObject } from '@/routes/process-objects/list/types';
import { DeploymentType, SWAP_REASONS_LIST } from '@/routes/measurement-group/field-report/types';
import { DeployDeviceFormSchemaType } from './deploy-device-schema';

type FieldReportFormFieldsProps = {
	form: UseFormReturn<DeployDeviceFormSchemaType>;
	availableProcessObjects: CompactProcessObject[];
	deploymentTypes: Promise<AxiosResponse<DeploymentType[]>>;
};

const FieldReportStep = (props: FieldReportFormFieldsProps) => {
	const { form: fieldReportForm, availableProcessObjects, deploymentTypes } = props;

	return (
		<div className="w-full flex flex-col gap-3 max-w-4xl">
			<FormField
				control={fieldReportForm.control}
				name="upload.processObjectId"
				render={({ field }) => (
					<FormItem>
						<FormLabel>Process object</FormLabel>

						<Select onValueChange={field.onChange} value={field.value ?? ''} disabled>
							<FormControl>
								<SelectTrigger className="w-full">
									<SelectValue placeholder="Select the process object" />
								</SelectTrigger>
							</FormControl>
							<Suspense
								fallback={
									<SelectContent>
										<Spinner />
									</SelectContent>
								}
							>
								<Await resolve={availableProcessObjects}>
									{availableProcessObjects => {
										const processObjects = availableProcessObjects.map(po => ({
											id: po.id,
											name: po.name
										}));
										return (
											<SelectContent>
												{processObjects.map(po => (
													<SelectItem key={po.id} value={po.id.toString()}>
														<span className="font-medium">{po.id}</span> - {po.name}
													</SelectItem>
												))}
											</SelectContent>
										);
									}}
								</Await>
							</Suspense>
						</Select>
						<FormMessage />
					</FormItem>
				)}
			/>

			<RHFDatePicker
				control={fieldReportForm.control}
				name="fieldReport.performedAt"
				label="Date"
				placeholder="Pick a date"
				isFullWidth
			/>

			<FormField
				control={fieldReportForm.control}
				name="fieldReport.deploymentTypeId"
				render={({ field }) => (
					<FormItem>
						<FormLabel>Deployment type</FormLabel>

						<Select onValueChange={field.onChange} value={field.value ?? ''}>
							<FormControl>
								<SelectTrigger className="w-full">
									<SelectValue placeholder="Select the deployment type" />
								</SelectTrigger>
							</FormControl>
							<Suspense
								fallback={
									<SelectContent>
										<Spinner />
									</SelectContent>
								}
							>
								<Await resolve={deploymentTypes}>
									{deploymentTypes => (
										<SelectContent>
											{deploymentTypes.data.map(t => (
												<SelectItem key={t.id} value={t.id.toString()}>
													{t.deploymentType}
												</SelectItem>
											))}
										</SelectContent>
									)}
								</Await>
							</Suspense>
						</Select>
						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={fieldReportForm.control}
				name="fieldReport.hasExternalAntennas"
				render={({ field }) => (
					<FormItem className="flex flex-row items-start space-x-3 space-y-0">
						<FormControl>
							<Checkbox checked={field.value} onCheckedChange={() => field.onChange(!field.value)} />
						</FormControl>
						<div className="space-y-1 leading-none">
							<FormLabel>Has external antennas</FormLabel>
						</div>
					</FormItem>
				)}
			/>

			<FormField
				control={fieldReportForm.control}
				name="fieldReport.swapReason"
				render={({ field }) => (
					<FormItem>
						<FormLabel>Swap reason</FormLabel>

						<Select onValueChange={field.onChange} value={field.value ?? ''}>
							<FormControl>
								<SelectTrigger className="w-full">
									<SelectValue placeholder="Select the swap reason" />
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								{SWAP_REASONS_LIST.map(t => (
									<SelectItem key={t.id} value={t.id.toString()}>
										{t.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={fieldReportForm.control}
				name="fieldReport.notes"
				render={({ field }) => (
					<FormItem>
						<FormLabel>Notes</FormLabel>
						<FormControl>
							<Textarea
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								name={field.name}
								placeholder="Add any notes here"
								rows={4}
								className="text-sm"
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	);
};

export default FieldReportStep;
